import { workspace } from '../../shared/api';

export interface MadicsSession {
  certificateData: MadicsCertificateData;
  statusMessage: string;
  hasMadicsConfiguration: boolean;
  hasCertificateInMadics: boolean;
  needToFillPin: boolean;
}

export interface MadicsCertificateData {
  activationType: string;
  enabled: boolean;
  validTo: Date;
  validFrom: Date;
  alias: string;
  subject: string;
  issuer: string;
}

export interface RemoteIDSessionData {
  TokenAcesso: string;
  TemSessao: boolean;
}

export interface RemoveIDAuthData {
  id: number;
  doctorId: number;
  creationDate: Date;
  emrId: number;
  expirationDate: Date;
  accessToken: string;
}

export const VIDAAS_DAYS_LIFE = 7;

export const CertificateService = {
  async checkSolutiStatus(doctorId: number): Promise<boolean> {
    const { data } = await workspace.get(`/soluti/medico/${doctorId}/sessao`);
    return !!data.hasActiveSession;
  },
  async authenticateSoluti(doctorId: number, otp: string) {
    const payload = {
      otp,
    };

    const { data } = await workspace.post(`/soluti/medico/${doctorId}/auth`, payload);
    return data;
  },
  vidaas: {
    async checkIfUserHasVidaas(prescriberDocument: string): Promise<boolean> {
      const params = {
        documento: prescriberDocument,
      };

      const { data } = await workspace.get('/valid/vidaas/descobrirusuario', { params });
      return !!data.PossuiCertificado;
    },
    async checkVidaasAuthorization(prescriberDocument: string): Promise<boolean> {
      const params = {
        documento: prescriberDocument,
      };

      const { data } = await workspace.get('/valid/vidaas/confirmarautorizacao', { params });
      return !!data;
    },

    async resetVidaasAuthorization(prescriberDocument: string, daysLife: number): Promise<boolean> {
      const params = {
        documento: prescriberDocument,
        dias: daysLife,
      };

      const { data } = await workspace.get('/valid/vidaas/reautorizar', { params });
      return !!data;
    },
    async authorizeVidaas(prescriberDocument: string, daysLife: number): Promise<boolean> {
      const params = {
        documento: prescriberDocument,
        dias: daysLife,
      };

      const { data } = await workspace.get('/valid/vidaas/solicitarautorizacao', { params });
      return !!data;
    },
  },
  safeweb: {
    async resetSafeIdSession(prescriberDocument: number): Promise<boolean> {
      const { data } = await workspace.delete(`/medico/sessao?idMedico=${prescriberDocument}`);
      return data;
    },
    async checkIfUserAuthorized(prescriberDocument: string): Promise<boolean> {
      const params = {
        documento: prescriberDocument,
      };

      const { data } = await workspace.get('/safeweb/safeid/consulta', { params });
      return !!data.certificado;
    },
    async checkIfUserHasSafeid(prescriberDocument: string): Promise<boolean> {
      const params = {
        documento: prescriberDocument,
      };

      const { data } = await workspace.get<{ PossuiCertificado: boolean }>('/safeweb/safeid/descobrirusuario', {
        params,
      });
      return !!data.PossuiCertificado;
    },
    /**
     * @param {string} prescriberDocument
     * @param {string} date YYYY-MM-DD format
     */
    async authorize(prescriberDocument: string, date: string): Promise<boolean> {
      const payload = {
        documento: prescriberDocument,
        data: date,
      };

      try {
        await workspace.post('/safeweb/safeid/authorize', payload);
        return true;
      } catch (e) {
        return false;
      }
    },
    async checkSession(prescriptionId: number): Promise<{ SessaoAtiva: boolean; Token: string | null }> {
      const params = {
        idPrescricao: prescriptionId,
      };

      const { data } = await workspace.get('/safeweb/safeid/sessao', { params });
      return data;
    },
    async authenticate(prescriptionId: number, otp: string) {
      const payload = {
        token: otp,
      };

      const { data } = await workspace.post(`/prescricao/${prescriptionId}/safeweb/auth`, payload);
      return data;
    },
  },
  certiSign: {
    async checkSession(prescriptionId: number) {
      const { data } = await workspace.get(`/prescricao/${prescriptionId}/assinatura-digital/certisign/sessao`);
      return data;
    },
    async authenticate(prescriptionId: number, payload: { pin?: string; otp?: string }) {
      const { data } = await workspace.post(
        `/prescricao/${prescriptionId}/assinatura-digital/certisign/sessao`,
        payload
      );
      return data;
    },
  },
  madics: {
    async checkSession(prescriptionId: number): Promise<MadicsSession> {
      const { data } = await workspace.get(`/digitalsignature/madics/session?idPrescricao=${prescriptionId}`);
      return data.data;
    },
  },
  dinamo: {
    async checkSession(prescriptionId: number): Promise<boolean> {
      const { data } = await workspace.get(`/prescricao/${prescriptionId}/dinamo/auth`);
      return data;
    },
    async authenticate(prescriptionId: number, password: string) {
      const payload = {
        password,
      };

      const { data } = await workspace.post(`/prescricao/${prescriptionId}/dinamo/auth`, payload);
      return data;
    },
  },
  remoteId: {
    async checkSession(prescriptionId: number): Promise<RemoteIDSessionData> {
      const { data } = await workspace.get(
        `/prescricao/${prescriptionId}/assinatura-digital/certisign/remote-id/sessao`
      );
      return data;
    },
    async authenticate(
      prescriptionId: number,
      payload: { pin?: string; token?: string; email?: string }
    ): Promise<RemoveIDAuthData> {
      const { data } = await workspace.post(
        `/prescricao/${prescriptionId}/assinatura-digital/certisign/remote-id/sessao`,
        payload
      );
      return data;
    },
  },
};
